import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Heart, Flower2, Target, DollarSign, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import TrustedProviders from '../components/TrustedProviders';
import PlaceFinder from '../components/PlaceFinder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FuneralPreferences() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSavingsTracker, setShowSavingsTracker] = useState(false);
  const [currentSavings, setCurrentSavings] = useState(0);
  const [preferences, setPreferences] = useState({
    burial_or_cremation: 'burial',
    ceremony_type: 'religious',
    location_preference: '',
    music_preferences: '',
    special_requests: '',
    estimated_cost: '',
    savings_goal_enabled: false,
    current_savings: 0
  });

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/funeral/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.preferences_id) {
        setPreferences({
          burial_or_cremation: response.data.burial_or_cremation || 'burial',
          ceremony_type: response.data.ceremony_type || 'religious',
          location_preference: response.data.location_preference || '',
          music_preferences: response.data.music_preferences || '',
          special_requests: response.data.special_requests || '',
          estimated_cost: response.data.estimated_cost || '',
          savings_goal_enabled: response.data.savings_goal_enabled || false,
          current_savings: response.data.current_savings || 0
        });
        setShowSavingsTracker(response.data.savings_goal_enabled || false);
        setCurrentSavings(response.data.current_savings || 0);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('session_token');
    setSaving(true);

    try {
      const payload = {
        ...preferences,
        estimated_cost: preferences.estimated_cost ? parseFloat(preferences.estimated_cost) : null,
        savings_goal_enabled: showSavingsTracker,
        current_savings: currentSavings
      };

      await axios.post(
        `${BACKEND_URL}/api/funeral/preferences`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Preferences saved successfully');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  // Calculate progress percentage
  const savingsProgress = preferences.estimated_cost && currentSavings 
    ? Math.min((currentSavings / parseFloat(preferences.estimated_cost)) * 100, 100)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#2B4C7E] border-r-transparent mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]" data-testid="funeral-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBF0F5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="rounded-full flex-shrink-0">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <h1 className="text-lg sm:text-2xl font-medium bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] bg-clip-text text-transparent truncate">Funeral Preferences</h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full hover:from-[#1E3A63] hover:to-[#1E3A63] flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4"
              data-testid="save-preferences-btn"
            >
              <Save className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
              <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save'}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
        <Card className="p-6 sm:p-8 rounded-2xl border border-[#EBF0F5] bg-white/90 backdrop-blur-md shadow-lg" data-testid="funeral-finder-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 bg-gradient-to-br from-[#EBF0F5] to-[#D7E0EA] rounded-xl flex items-center justify-center shrink-0">
              <Flower2 className="h-5 w-5 text-[#2B4C7E]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Find a Funeral Home Near You</h2>
              <p className="text-sm text-gray-500">Search funeral homes by ZIP code, city, or your current location.</p>
            </div>
          </div>
          <PlaceFinder
            category="funeral_home" noun="funeral homes" saveLabel="Save"
            savedTitle="My saved funeral homes" testid="funeral-finder"
            disclaimer="Listings are for convenience only. EasyPassing does not endorse, guarantee, or control any funeral home or provider."
          />
        </Card>

        <Card className="p-8 rounded-2xl border border-[#EBF0F5] bg-white/90 backdrop-blur-md shadow-lg">
          <div className="flex items-start gap-6 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#EBF0F5] to-[#D7E0EA] rounded-xl flex items-center justify-center flex-shrink-0">
              <Heart className="h-8 w-8 text-[#2B4C7E]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-2xl font-medium text-gray-800 mb-2">Plan Your Final Wishes</h2>
              <p className="text-gray-600">
                Share your preferences for your funeral or memorial service to help your loved ones honor your wishes.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Burial or Cremation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Burial or Cremation</label>
              <Select value={preferences.burial_or_cremation} onValueChange={(value) => setPreferences({ ...preferences, burial_or_cremation: value })}>
                <SelectTrigger data-testid="burial-cremation-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="burial">Burial</SelectItem>
                  <SelectItem value="cremation">Cremation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ceremony Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type of Ceremony</label>
              <Select value={preferences.ceremony_type} onValueChange={(value) => setPreferences({ ...preferences, ceremony_type: value })}>
                <SelectTrigger data-testid="ceremony-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="religious">Religious Service</SelectItem>
                  <SelectItem value="secular">Secular/Non-Religious</SelectItem>
                  <SelectItem value="celebration_of_life">Celebration of Life</SelectItem>
                  <SelectItem value="none">No Formal Service</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Location Preference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location Preference</label>
              <Input
                placeholder="e.g., St. Mary's Church, Ocean View Memorial Park"
                value={preferences.location_preference}
                onChange={(e) => setPreferences({ ...preferences, location_preference: e.target.value })}
                data-testid="location-input"
              />
            </div>

            {/* Music Preferences */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Music Preferences</label>
              <Textarea
                placeholder="Songs, hymns, or specific artists you'd like played..."
                value={preferences.music_preferences}
                onChange={(e) => setPreferences({ ...preferences, music_preferences: e.target.value })}
                rows={4}
                data-testid="music-textarea"
              />
            </div>

            {/* Special Requests */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Special Requests</label>
              <Textarea
                placeholder="Any other specific wishes, dress code, flowers, donations in lieu of flowers, etc..."
                value={preferences.special_requests}
                onChange={(e) => setPreferences({ ...preferences, special_requests: e.target.value })}
                rows={6}
                data-testid="special-requests-textarea"
              />
            </div>

            {/* Estimated Cost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Cost</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={preferences.estimated_cost}
                  onChange={(e) => setPreferences({ ...preferences, estimated_cost: e.target.value })}
                  className="pl-7"
                  data-testid="cost-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">This helps your family plan financially</p>
            </div>

            {/* Savings Goal Tracker Toggle */}
            {preferences.estimated_cost && parseFloat(preferences.estimated_cost) > 0 && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="pt-4 border-t border-gray-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
                      <Target className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Savings Goal Tracker</p>
                      <p className="text-xs text-gray-500">Track your progress towards funeral costs</p>
                    </div>
                  </div>
                  <Switch
                    checked={showSavingsTracker}
                    onCheckedChange={setShowSavingsTracker}
                    data-testid="savings-tracker-toggle"
                  />
                </div>

                {showSavingsTracker && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                  >
                    {/* Current Savings - Linked to Funeral Fund */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Savings
                        <span className="ml-2 text-xs text-green-600 font-normal">(from Funeral Fund)</span>
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          value={`$${currentSavings.toLocaleString()}`}
                          readOnly
                          className="pl-9 bg-gray-50 cursor-not-allowed"
                          data-testid="current-savings-input"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <a href="/savings" className="text-[#2B4C7E] hover:underline flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          Track savings in Savings &amp; Beneficiary Planning
                        </a>
                      </p>
                    </div>

                    {/* Progress Bar */}
                    <Card className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Progress</span>
                        <span className="text-sm font-bold text-green-600">{savingsProgress.toFixed(1)}%</span>
                      </div>
                      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${savingsProgress}%` }}
                          transition={{ duration: 0.5, ease: 'easeOut' }}
                          className={`h-full rounded-full ${
                            savingsProgress >= 100 
                              ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                              : savingsProgress >= 50 
                                ? 'bg-gradient-to-r from-yellow-500 to-[#C8A97E]'
                                : 'bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E]'
                          }`}
                        />
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span>${currentSavings.toLocaleString()} saved</span>
                        <span>${parseFloat(preferences.estimated_cost).toLocaleString()} goal</span>
                      </div>
                      {savingsProgress >= 100 && (
                        <div className="mt-3 flex items-center gap-2 text-green-600 text-sm font-medium">
                          <TrendingUp className="h-4 w-4" />
                          Goal reached! Your family is covered.
                        </div>
                      )}
                      {savingsProgress < 100 && (
                        <p className="mt-2 text-xs text-gray-500">
                          ${(parseFloat(preferences.estimated_cost) - currentSavings).toLocaleString()} remaining to reach your goal
                        </p>
                      )}
                    </Card>
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>
        </Card>

        {/* Trusted Funeral Providers Section */}
        <motion.div 
          className="mt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <TrustedProviders 
            category="funeral"
            title="Browse Trusted Funeral Providers"
            description="Compare reputable funeral homes, cremation services, and memorial parks"
            icon={Flower2}
          />
        </motion.div>

        {/* Information Card */}
        <Card className="mt-8 p-6 rounded-2xl border border-[#EBF0F5] bg-gradient-to-br from-[#EBF0F5] to-white">
          <h3 className="text-lg font-medium text-gray-800 mb-3">Why This Matters</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span>Reduces stress on your family during a difficult time</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span>Ensures your wishes are honored</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span>Helps with financial planning</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span>Provides clear guidance for your loved ones</span>
            </li>
          </ul>
        </Card>
      </main>
    </div>
  );
}